import * as React from "react";
import { motion, useMotionValue, useSpring, useMotionTemplate } from "framer-motion";
import { cn } from "@/lib/utils";

type Props = {
  className?: string;
  /** Spotlight radius in px. */
  size?: number;
  /** Colour of the spotlight (rgba ideally — controls intensity). */
  color?: string;
};

/**
 * Aceternity-style spotlight that follows the cursor with smoothed
 * spring motion. Initial position is the centre of the container;
 * pointer move pulls it around. Render INSIDE a `relative` parent
 * and let it absolute-fill.
 */
export function Spotlight({
  className,
  size = 500,
  color = "rgba(201, 169, 106, 0.20)"
}: Props) {
  const ref = React.useRef<HTMLDivElement>(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springX = useSpring(mouseX, { stiffness: 120, damping: 22, mass: 0.6 });
  const springY = useSpring(mouseY, { stiffness: 120, damping: 22, mass: 0.6 });

  React.useEffect(() => {
    const r = ref.current?.getBoundingClientRect();
    if (r) { mouseX.set(r.width / 2); mouseY.set(r.height / 3); }
  }, [mouseX, mouseY]);

  React.useEffect(() => {
    const handler = (e: PointerEvent) => {
      const r = ref.current?.getBoundingClientRect();
      if (!r) return;
      mouseX.set(e.clientX - r.left);
      mouseY.set(e.clientY - r.top);
    };
    window.addEventListener("pointermove", handler, { passive: true });
    return () => window.removeEventListener("pointermove", handler);
  }, [mouseX, mouseY]);

  const bg = useMotionTemplate`radial-gradient(${size}px circle at ${springX}px ${springY}px, ${color}, transparent 70%)`;

  return (
    <motion.div
      ref={ref}
      aria-hidden
      className={cn("absolute inset-0 pointer-events-none", className)}
      style={{ background: bg }}
    />
  );
}
